import React from 'react';
import styled, { keyframes } from 'styled-components';
import packageJson from '../../../package.json';
import { useTheme } from '../../contexts/ThemeContext';

const fadeIn = keyframes`
  from { opacity: 0; transform: scale(0.97) translateY(8px); }
  to   { opacity: 1; transform: scale(1)    translateY(0);   }
`;

const Overlay = styled.div`
  position: fixed;
  inset: 0;
  background: rgba(0, 0, 0, 0.65);
  backdrop-filter: blur(10px);
  -webkit-backdrop-filter: blur(10px);
  display: ${p => p.$show ? 'flex' : 'none'};
  align-items: center;
  justify-content: center;
  z-index: 9999;
  padding: 24px;
`;

const Modal = styled.div`
  background: ${p => p.theme.colors.surface};
  border: 1px solid ${p => p.theme.colors.border};
  border-radius: 20px;
  padding: 32px;
  max-width: 720px;
  width: 100%;
  box-shadow: 0 32px 80px rgba(0, 0, 0, 0.5);
  animation: ${fadeIn} 0.22s cubic-bezier(0.25, 0.46, 0.45, 0.94) forwards;
`;

const Header = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-bottom: 28px;
`;

const TitleGroup = styled.div`
  display: flex;
  align-items: center;
  gap: 10px;
`;

const StatusDot = styled.div`
  width: 10px;
  height: 10px;
  border-radius: 50%;
  flex-shrink: 0;
  background: ${p => p.$green ? '#22c55e' : p.$orange ? '#f97316' : '#ef4444'};
  box-shadow: 0 0 8px ${p => p.$green ? 'rgba(34,197,94,0.5)' : p.$orange ? 'rgba(249,115,22,0.5)' : 'rgba(239,68,68,0.5)'};
`;

const Title = styled.h2`
  color: ${p => p.theme.colors.text};
  margin: 0;
  font-size: 1.1rem;
  font-weight: 700;
  letter-spacing: -0.02em;
`;

const BadgeRow = styled.div`
  display: flex;
  gap: 8px;
`;

const Badge = styled.span`
  font-size: 0.7rem;
  font-weight: 600;
  padding: 3px 10px;
  border-radius: 100px;
  letter-spacing: 0.04em;
  background: ${p =>
    p.$green ? 'rgba(34,197,94,0.12)' :
    p.$orange ? 'rgba(249,115,22,0.12)' :
    p.$red ? 'rgba(239,68,68,0.12)' :
    p.$blue ? 'rgba(96,165,250,0.12)' :
    'rgba(255,255,255,0.07)'};
  color: ${p =>
    p.$green ? '#22c55e' :
    p.$orange ? '#f97316' :
    p.$red ? '#ef4444' :
    p.$blue ? '#60a5fa' :
    p.theme.colors.muted};
  border: 1px solid ${p =>
    p.$green ? 'rgba(34,197,94,0.2)' :
    p.$orange ? 'rgba(249,115,22,0.2)' :
    p.$red ? 'rgba(239,68,68,0.2)' :
    p.$blue ? 'rgba(96,165,250,0.2)' :
    p.theme.colors.border};
`;

const Grid = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 16px;
  margin-bottom: 16px;

  @media (max-width: 540px) { grid-template-columns: 1fr; }
`;

const Section = styled.div`
  background: ${p => p.theme.mode === 'light' ? 'rgba(0,0,0,0.025)' : 'rgba(255,255,255,0.025)'};
  border: 1px solid ${p => p.theme.colors.border};
  border-radius: 14px;
  overflow: hidden;
`;

const SectionHeader = styled.div`
  padding: 10px 16px;
  border-bottom: 1px solid ${p => p.theme.colors.border};
  font-size: 0.68rem;
  font-weight: 700;
  letter-spacing: 0.09em;
  text-transform: uppercase;
  color: ${p => p.theme.colors.muted};
  opacity: 0.6;
`;

const Row = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 10px 16px;

  &:not(:last-child) {
    border-bottom: 1px solid ${p => p.theme.colors.border};
  }
`;

const RowLabel = styled.span`
  color: ${p => p.theme.colors.muted};
  font-size: 0.82rem;
  font-weight: 400;
  opacity: 0.75;
`;

const RowValue = styled.span`
  font-size: 0.82rem;
  font-weight: 600;
  display: flex;
  align-items: center;
  gap: 6px;
  color: ${p =>
    p.$green ? '#22c55e' :
    p.$orange ? '#f97316' :
    p.$red ? '#ef4444' :
    p.$blue ? '#60a5fa' :
    p.theme.colors.text};
`;

const Dot = styled.span`
  display: inline-block;
  width: 6px;
  height: 6px;
  border-radius: 50%;
  background: ${p =>
    p.$green ? '#22c55e' :
    p.$orange ? '#f97316' :
    p.$red ? '#ef4444' :
    p.theme.colors.muted};
`;

const FullSection = styled(Section)`
  margin-bottom: 16px;
  grid-column: 1 / -1;
`;

const ButtonRow = styled.div`
  display: flex;
  gap: 10px;
  margin-top: 24px;
`;

const CloseBtn = styled.button`
  flex: 1;
  padding: 12px;
  border: 1px solid ${p => p.theme.colors.border};
  border-radius: 100px;
  font-weight: 500;
  font-size: 0.9rem;
  cursor: pointer;
  background: transparent;
  color: ${p => p.theme.colors.muted};
  transition: all 0.2s ease;

  &:hover {
    background: ${p => p.theme.mode === 'light' ? 'rgba(0,0,0,0.04)' : 'rgba(255,255,255,0.05)'};
    color: ${p => p.theme.colors.text};
    border-color: ${p => p.theme.colors.text};
  }
`;

const RefreshBtn = styled.button`
  flex: 1;
  padding: 12px;
  border: none;
  border-radius: 100px;
  font-weight: 600;
  font-size: 0.9rem;
  cursor: pointer;
  background: #f97316;
  color: #fff;
  transition: filter 0.2s ease;

  &:hover { filter: brightness(0.88); }
  &:disabled { opacity: 0.45; cursor: not-allowed; }
`;

const StatusPopup = ({ show, lastUpdateTime, shopStatus, activeOrderCount = 0, onClose, onForceSync, forceSyncing }) => {
  useTheme();

  if (!show) return null;

  const fmt     = d => d.toLocaleTimeString('nl-NL', { hour: '2-digit', minute: '2-digit', second: '2-digit' });
  const fmtDate = d => d.toLocaleDateString('nl-NL', { weekday: 'long', day: 'numeric', month: 'long', year: 'numeric' });

  const isOffline    = (Date.now() - lastUpdateTime.getTime()) > 10 * 60 * 1000;
  const shopOpen     = shopStatus?.isOpen ?? false;
  const isLightPoll  = !shopOpen && activeOrderCount === 0;
  const nextPollTime = new Date(lastUpdateTime.getTime() + 300 * 1000);
  const minsAgo      = Math.floor((Date.now() - lastUpdateTime.getTime()) / 60000);

  const pollLabel = isOffline ? 'Offline' : isLightPoll ? 'Light poll' : 'Full sync';
  const pollDesc  = isLightPoll ? 'Alleen winkelstatus' : 'Orders + winkelstatus';

  const sourceLabels = {
    opening_hours: 'Openingstijden',
    manual_close:  'Handmatig gesloten',
    no_hours:      'Geen uren gevonden',
    error:         'Fout bij ophalen',
    Unknown:       'Onbekend',
  };

  const connStatus = isOffline ? 'Offline' : 'Verbonden';

  return (
    <Overlay $show={show} onClick={onClose}>
      <Modal onClick={e => e.stopPropagation()}>

        <Header>
          <TitleGroup>
            <StatusDot $green={!isOffline && !isLightPoll} $orange={!isOffline && isLightPoll} $red={isOffline} />
            <Title>Verbinding & Status</Title>
          </TitleGroup>
          <BadgeRow>
            <Badge $blue>v{packageJson.version}</Badge>
            <Badge $green={!isOffline && !isLightPoll} $orange={!isOffline && isLightPoll} $red={isOffline}>
              {connStatus}
            </Badge>
            {isLightPoll && !isOffline && <Badge $orange>Standby</Badge>}
          </BadgeRow>
        </Header>

        <Grid>
          {/* Polling */}
          <Section>
            <SectionHeader>Polling</SectionHeader>
            <Row>
              <RowLabel>Modus</RowLabel>
              <RowValue $orange={isLightPoll && !isOffline} $green={!isLightPoll && !isOffline} $red={isOffline}>
                <Dot $orange={isLightPoll && !isOffline} $green={!isLightPoll && !isOffline} $red={isOffline} />
                {pollLabel}
              </RowValue>
            </Row>
            <Row>
              <RowLabel>Ophalen</RowLabel>
              <RowValue>{isOffline ? '—' : pollDesc}</RowValue>
            </Row>
            <Row>
              <RowLabel>Interval</RowLabel>
              <RowValue>5 minuten</RowValue>
            </Row>
          </Section>

          {/* Tijden */}
          <Section>
            <SectionHeader>Tijden</SectionHeader>
            <Row>
              <RowLabel>Laatste update</RowLabel>
              <RowValue>{fmt(lastUpdateTime)}</RowValue>
            </Row>
            <Row>
              <RowLabel>Geleden</RowLabel>
              <RowValue $orange={minsAgo >= 4} $red={isOffline}>{minsAgo === 0 ? 'Zojuist' : `${minsAgo} min geleden`}</RowValue>
            </Row>
            <Row>
              <RowLabel>Volgende poll</RowLabel>
              <RowValue>{fmt(nextPollTime)}</RowValue>
            </Row>
          </Section>

          {/* Winkel */}
          <Section>
            <SectionHeader>Winkel</SectionHeader>
            <Row>
              <RowLabel>Status</RowLabel>
              <RowValue $green={shopOpen} $red={!shopOpen}>
                <Dot $green={shopOpen} $red={!shopOpen} />
                {shopOpen ? 'Open' : 'Gesloten'}
              </RowValue>
            </Row>
            {shopStatus?.source && (
              <Row>
                <RowLabel>Bron</RowLabel>
                <RowValue>{sourceLabels[shopStatus.source] ?? shopStatus.source}</RowValue>
              </Row>
            )}
            {shopStatus?.openTime && (
              <Row>
                <RowLabel>Openingstijden</RowLabel>
                <RowValue>{shopStatus.openTime} – {shopStatus.closeTime}</RowValue>
              </Row>
            )}
          </Section>

          {/* Orders */}
          <Section>
            <SectionHeader>Orders</SectionHeader>
            <Row>
              <RowLabel>Actief</RowLabel>
              <RowValue $green={activeOrderCount > 0}>{activeOrderCount}</RowValue>
            </Row>
            <Row>
              <RowLabel>Datum</RowLabel>
              <RowValue>{fmtDate(lastUpdateTime)}</RowValue>
            </Row>
          </Section>
        </Grid>

        <ButtonRow>
          <CloseBtn onClick={onClose}>Sluiten</CloseBtn>
          {isLightPoll && !isOffline && (
            <RefreshBtn onClick={onForceSync} disabled={forceSyncing}>
              {forceSyncing ? 'Bezig...' : 'Refresh volledig'}
            </RefreshBtn>
          )}
        </ButtonRow>

      </Modal>
    </Overlay>
  );
};

export default StatusPopup;
